import { COLLECTION_TYPE } from '@utils/constants';

import { apiClientV2, unwrapV2Response } from './index.js';

/**
 * Object-picker "collections" API (v2).
 *
 * Endpoints (the `/v2` prefix is part of the apiClientV2 base URL):
 *   GET    /collections           - list all collections
 *   POST   /collections           - create a collection
 *   PATCH  /collections/{uuid}    - update a collection
 *   DELETE /collections/{uuid}    - delete a collection
 *
 * v2 responses are shaped `{ success, message, data, errors }` and are unwrapped
 * with `unwrapV2Response`. The list lives under `data.collections`.
 *
 * Backend collection shape:
 *   {
 *     uuid, name, mode, scope, user_uuid, org_uuid, created_at, updated_at,
 *     detection_objects: [{ node_uuid, name, filters, style, is_active }],
 *     filters,
 *     hexagon
 *   }
 *
 * `hexagon` is a free-form object holding the collection's hexagon display
 * settings (`{ size, color }` today — the backend saves and returns it without
 * reading its contents). A collection saved before the field existed returns
 * `{}`. A PATCH replaces the whole object, and an explicit `null` is rejected
 * with 400, so it is only ever sent when there is a value to send. Normalizing
 * what comes back is `readHexagonConfig`'s job, in the collection results stack.
 *
 * The UI works with a normalized shape:
 *   { id, name, type, mode, objects: [{ key, label, color }] }
 * where `type` is the backend `scope` verbatim (see COLLECTION_TYPE).
 *
 * Object identity — the one rule every collection module follows:
 *   - A collection object's `key` is its taxonomy node uuid (`node_uuid` in the API),
 *     and its `label` is the collection's own `name` for it.
 *   - A taxonomy node also has a `key`, but that is its slug (`spruce`). It never
 *     identifies a collection object: match taxonomy nodes by `uuid`.
 *   - Detection features, hexagon cells and sampling plots name objects by node
 *     uuid only; `objectLabel` (collection results `transformUtils`) turns one into
 *     a display name.
 *
 * All field-name mapping is isolated in `normalizeCollection` / `normalizeObject`
 * / `toApiPayload` — adjust those in one place if the payload changes.
 */

/**
 * Normalize one `detection_objects` entry. The backend identifies the object by
 * its taxonomy `node_uuid` and stores the collection's own `name` for it, plus a
 * per-object `style` (color lives under `style`).
 */
const normalizeObject = raw => ({
	key: raw?.node_uuid ?? '',
	label: raw?.name ?? '',
	color: raw?.style?.color,
});

// Backend `scope` → UI collection type. This map is the single place that
// translates the backend scope; unknown scopes fall back to personal.
const SCOPE_TO_TYPE = {
	system: COLLECTION_TYPE.TEMPLATE,
	org: COLLECTION_TYPE.ORG,
	user: COLLECTION_TYPE.PERSONAL,
};

/**
 * Normalize a backend collection into the shape the Object Picker UI expects.
 * `id` maps to the backend `uuid` (used for PATCH/DELETE).
 */
export const normalizeCollection = raw => ({
	id: raw?.uuid ?? raw?.id,
	name: raw?.name ?? '',
	mode: raw?.mode,
	type: SCOPE_TO_TYPE[raw?.scope] ?? COLLECTION_TYPE.PERSONAL,
	objects: (raw?.detection_objects ?? []).map(normalizeObject),
	// The raw backend entries (with their original `style`/`filters`), kept so an
	// update can preserve unchanged objects exactly as they were stored.
	detectionObjects: raw?.detection_objects ?? [],
});

/**
 * Build the request body for create/update.
 *
 * Objects are sent under `detection_objects` as `{ node_uuid, name, style }`: the
 * object's `key` is its node uuid, its `label` the required, non-blank `name`,
 * and its color lives at `style.color`. The API rejects any other field with 400.
 * `mode` is only included when provided so a PATCH doesn't clobber it.
 */
const toApiPayload = ({ name, objects, mode } = {}) => ({
	...(name !== undefined ? { name } : {}),
	...(mode !== undefined ? { mode } : {}),
	...(objects !== undefined
		? {
				detection_objects: objects.map(({ key, label, color }) => ({
					node_uuid: key,
					name: label,
					style: color ? { color } : {},
				})),
			}
		: {}),
});

/**
 * Fetch every collection.
 * @returns {Promise<Array>}
 */
export const getCollections = async () => {
	const { data, error } = await apiClientV2.get('/collections');
	if (error) throw error;
	const payload = unwrapV2Response(data);
	const list = Array.isArray(payload) ? payload : (payload?.collections ?? []);
	return list.map(normalizeCollection);
};

/**
 * Create a new collection.
 * @param {{ name: string, objects: Array<{key: string, label: string, color?: string}>, mode?: string }} payload
 *   `key` is the taxonomy node uuid; `label` is sent as the object's `name`
 */
export const createCollection = async payload => {
	const { data, error } = await apiClientV2.post('/collections', toApiPayload(payload));
	if (error) throw error;
	const result = unwrapV2Response(data);
	return normalizeCollection(result?.collection ?? result);
};

/**
 * Update a collection, identified by its `uuid`.
 * @param {string} id - the collection uuid
 * @param {object} payload
 * @param {string} [payload.name]
 * @param {object} [payload.filters] - collection-level filters, sent as-is
 * @param {Array<object>} [payload.detectionObjects] - pre-built detection_objects array,
 *   sent as-is; it replaces the stored list. The object set is fixed at creation, so
 *   this may only re-style or re-filter the stored objects. Omit it to leave them alone.
 * @param {string} [payload.mode]
 * @param {object} [payload.hexagon] - hexagon display settings, sent as-is. Replaces the
 *   whole stored object, so callers must pass the merged result rather than only the keys
 *   they changed. Omit to leave the stored value untouched; `null` is rejected with 400.
 */
export const updateCollection = async (
	id,
	{ name, filters, detectionObjects, mode, hexagon } = {}
) => {
	const body = {
		...(name !== undefined ? { name } : {}),
		...(mode !== undefined ? { mode } : {}),
		...(filters != null ? { filters } : {}),
		// `!= null` rather than `!== undefined`: an explicit null is a 400, so a
		// caller that ended up with one is better served by leaving the stored
		// value alone than by a rejected request.
		...(hexagon != null ? { hexagon } : {}),
		...(detectionObjects !== undefined ? { detection_objects: detectionObjects } : {}),
	};
	const { data, error } = await apiClientV2.patch(`/collections/${id}`, body);
	if (error) throw error;
	const result = unwrapV2Response(data);
	return normalizeCollection(result?.collection ?? result);
};

/**
 * Delete a collection, identified by its `uuid`.
 * @param {string} id - the collection uuid
 */
export const deleteCollection = async id => {
	const { data, error } = await apiClientV2.delete(`/collections/${id}`);
	if (error) throw error;
	unwrapV2Response(data);
	return { id };
};

/**
 * Run detection for a collection's objects on a project.
 * POST /collections/{collectionUuid}/detect
 *
 * The endpoint is two-phase: without `confirm` nothing is started, and the
 * response describes what *would* happen. There are three outcomes, told apart
 * by `ran` / `requires_confirmation` (see `@utils/detectionOutcome`):
 *
 *   - already detected     — `ran: false`, `requires_confirmation: false`. Every
 *     object in the collection has already been detected over this ground, so
 *     nothing is started. `confirm` is ignored for this outcome — a confirmed
 *     request over covered ground still starts nothing.
 *   - must run, not confirmed — `ran: false`, `requires_confirmation: true`, with
 *     `run_preview.scan_area_m2` describing the ground a run would scan
 *     (`run_preview` is null when that ground can't be measured yet). Repeat the
 *     request with `confirm: true` to start it.
 *   - job started          — `ran: true`, `job_uuid` set.
 *
 * @param {string} collectionUuid - the collection to run
 * @param {object} params
 * @param {string} params.projectUuid - project to run detection on
 * @param {Array<[number, number]> | 'all'} params.aoi - polygon ring (EPSG:3857)
 *   to detect within, or `'all'` for the entire project
 * @param {boolean} [params.confirm] - `true` starts the run, `false` previews it.
 *   Both callers pass this explicitly; a non-boolean is omitted from the body,
 *   which the server also reads as a preview.
 */
export const detectCollection = async (collectionUuid, { projectUuid, aoi, confirm }) => {
	const { data, error } = await apiClientV2.post(`/collections/${collectionUuid}/detect`, {
		project_uuid: projectUuid,
		aoi,
		...(typeof confirm === 'boolean' ? { confirm } : {}),
	});
	if (error) throw error;
	return unwrapV2Response(data);
};

/**
 * Get collections that have been run on a project.
 * GET /projects/{projectUuid}/collections
 *
 * @param {string} projectUuid - the project UUID
 * @returns {Promise<Array>} List of collection results for this project
 */
export const getProjectCollections = async projectUuid => {
	const { data, error } = await apiClientV2.get(`/projects/${projectUuid}/collections`);
	if (error) throw error;
	const payload = unwrapV2Response(data);
	return payload?.collections ?? [];
};

/**
 * Get collection details by UUID.
 * GET /collections/{collectionUuid}
 *
 * @param {string} collectionUuid - the collection UUID
 * @returns {Promise<object>} Collection details (uuid, name, mode, detection_objects, filters)
 */
export const getCollectionDetails = async collectionUuid => {
	const { data, error } = await apiClientV2.get(`/collections/${collectionUuid}`);
	if (error) throw error;
	return unwrapV2Response(data);
};

/**
 * Get detection results for a collection on a specific project.
 * POST /collections/{collectionUuid}/detections
 *
 * The `projection` field controls the coordinate space of the returned GeoJSON:
 *   - '3857'       → EPSG:3857 (Web Mercator) — use for orthophoto map rendering
 *   - 'normalized' → [0–1] image-space coords  — use for single-image click-and-view
 *
 * When omitted the backend defaults per collection mode:
 *   orthophoto → '3857', single_image → 'normalized'.
 * Requesting 'normalized' on an orthophoto collection is rejected with 422.
 * The effective value is echoed back as `metadata.projection` in the response.
 *
 * @param {string} projectUuid    - the project UUID
 * @param {string} collectionUuid - the collection UUID
 * @param {string} [projection]   - coordinate space: '3857' (default) or 'normalized'
 * @returns {Promise<object>} Detection results with metadata and GeoJSON features
 */
export const getCollectionDetections = async (projectUuid, collectionUuid, projection = '3857') => {
	const { data, error } = await apiClientV2.post(`/collections/${collectionUuid}/detections`, {
		project_uuid: projectUuid,
		projection,
	});
	if (error) throw error;
	return unwrapV2Response(data);
};

/**
 * Get the collection's detected-area boundary for a project — the GeoJSON
 * polygon of the region that was actually scanned, mirroring the per-model
 * `features/detected_areas` feature type.
 *
 * GET /collections/{collectionUuid}/detected-area?project_uuid={projectUuid}
 *
 * The backend produces and uploads this boundary to S3 during the collection
 * detection pipeline; it 404s if the collection hasn't produced one yet for
 * this project. That's a normal "nothing to show" state rather than a hard
 * failure, so it resolves to `[]` instead of throwing.
 *
 * @param {string} projectUuid    - the project UUID
 * @param {string} collectionUuid - the collection UUID
 * @returns {Promise<Array>} GeoJSON features (empty if no boundary exists yet)
 */
export const getCollectionDetectedArea = async (projectUuid, collectionUuid) => {
	const { data, error } = await apiClientV2.get(`/collections/${collectionUuid}/detected-area`, {
		project_uuid: projectUuid,
	});
	if (error) {
		// 404 means no boundary has been computed yet — a normal empty state.
		if (error.status === 404) return [];
		throw error;
	}
	return unwrapV2Response(data)?.features ?? [];
};

/**
 * Aggregate a collection's detections into hexagon cells for a project.
 * POST /collections/{collectionUuid}/hexagons?resolution={resolution}
 *
 * Mirrors {@link getCollectionDetections} / {@link getCollectionDetectedArea}:
 * same project scoping, same GeoJSON output — one Feature per populated cell,
 * always in EPSG:3857 so cells drop straight onto the orthophoto map. The cells
 * cover the same viewport as `/detections`, so hexagons and detection shapes
 * describe the same region.
 *
 * The `filter` body is the only filter input: a per-object map
 * `{ [node_uuid]: { confidence, height, area, radius } }` where each range is
 * `{ min, max }` (inclusive, null = open-ended). Object keys present in the map
 * are the ones aggregated — an omitted/empty map aggregates every object with no
 * range predicates. Build it from the persisted collection filters with
 * `buildHexagonFilterBody` so the SQL aggregation agrees with the client-side
 * detection list (see that helper for the count-agreement rationale).
 *
 * Empty states (resolve to `[]` rather than throwing, so callers treat them as
 * "nothing to show"):
 *   - 404 → the collection has no detected area in this project yet.
 *   - 204 → a detected area exists but nothing survives the filter.
 *
 * @param {string} projectUuid    - the project UUID to aggregate within
 * @param {string} collectionUuid - the collection UUID
 * @param {number} [resolution=1000] - hexagon area in m²
 * @param {object} [filter={}]     - per-object range map (see above)
 * @returns {Promise<Array>} GeoJSON Feature array (EPSG:3857); empty when there
 *   is no detected area or nothing survives the filter
 */
export const getCollectionHexagons = async (
	projectUuid,
	collectionUuid,
	resolution = 1000,
	filter = {}
) => {
	const { data, error } = await apiClientV2.post(
		`/collections/${collectionUuid}/hexagons`,
		{ project_uuid: projectUuid, filter },
		{ resolution }
	);
	if (error) {
		// No detected area for this collection/project yet — a normal empty state.
		if (error.status === 404) return [];
		throw error;
	}
	// 204 No Content: a detected area exists but nothing survives the filter.
	// HTTPClient surfaces a no-content body as an empty string, so any non-object
	// payload is the empty case.
	if (!data || typeof data !== 'object') return [];
	// Success: a GeoJSON FeatureCollection. Callers only need the features — the
	// crs is fixed (EPSG:3857) and already matches the map projection.
	return unwrapV2Response(data)?.features ?? [];
};

/**
 * Aggregate a collection's detections into digital sampling plots (DSP) for a
 * project — one plot per drone image footprint.
 * POST /collections/{collectionUuid}/dsp
 *
 * Forest inventory tallies trees inside a fixed sample plot and scales to a
 * hectare; this does the same with drone photography. Each image the
 * collection's last detection run processed is a plot, the detections inside it
 * are the tally, and `properties.area_m2` (measured in EPSG:6933) is the plot
 * size. The returned Feature geometry is EPSG:3857, matching the detections
 * drawn alongside it.
 *
 * **Single-image only.** A plot is an image, so orthophoto imagery has no plots:
 * the project's `image_mode` and the collection's `mode` must both be
 * `single_image` or the request is rejected with 422 before any database work.
 * Use {@link getCollectionHexagons} for orthophoto — a lattice needs no
 * per-image footprints.
 *
 * The `filter` body is the only filter input, identical in shape and semantics
 * to {@link getCollectionHexagons} (inclusive bounds, `null` = open-ended, a
 * NULL value excludes the detection, keys present = the set to aggregate). Build
 * it with `buildHexagonFilterBody`. There is no `resolution`: a plot's size is
 * the image footprint, not a caller choice. A filter key the collection doesn't
 * have is rejected with 422 naming it.
 *
 * Two differences from `/hexagons` that matter when reading the numbers:
 *   - **Duplicates are kept.** `/hexagons` keeps only canonical rows; here a
 *     tree photographed by two overlapping images is a real observation in both
 *     plots. So Σ count across plots exceeds the collection's object count by
 *     the image overlap — correct for per-plot density, wrong as a survey total.
 *   - **Footprints overlap.** Σ `area_m2` double-counts ground and is not the
 *     survey area; `/detected-area` reports the union as `covered_area_m2`.
 *
 * There is no 204 — every image in the viewport is reported, so a filter
 * matching nothing still returns every plot with zeroed counts. 404 (no
 * detected area in this project yet) resolves to `[]` like the sibling
 * endpoints; 422 throws, since it means the caller sent something invalid.
 *
 * @param {string} projectUuid    - the project UUID to aggregate within
 * @param {string} collectionUuid - the collection UUID
 * @param {object} [filter={}]    - per-object range map (see above)
 * @returns {Promise<Array>} GeoJSON Feature array (EPSG:3857), one per image,
 *   ordered by tile_name; empty when there is no detected area yet
 */
export const getCollectionDsp = async (projectUuid, collectionUuid, filter = {}) => {
	const { data, error } = await apiClientV2.post(`/collections/${collectionUuid}/dsp`, {
		project_uuid: projectUuid,
		filter,
	});
	if (error) {
		// No detected area for this collection/project yet — a normal empty state.
		if (error.status === 404) return [];
		throw error;
	}
	// Defensive: the endpoint has no 204, but a non-object body can't be unwrapped.
	if (!data || typeof data !== 'object') return [];
	return unwrapV2Response(data)?.features ?? [];
};

/**
 * POST one collection export and answer with the storage key the server wrote.
 *
 * The detections and sampling-plot exports share a request schema, a key-shaped
 * response and the same 204 contract, so the two public functions below differ
 * only in their path — this holds the body they have in common.
 *
 * 204 (a detected area exists, but no detection passes the filter) resolves to
 * `null` rather than throwing: HTTPClient surfaces a no-content body as an empty
 * string, and that empty string is the only shape treated as "nothing passed
 * the filter". Any other payload that isn't a proper `{ key }` envelope — a
 * missing key, a non-string key, or a body of the wrong type — throws, since
 * that means the response doesn't match the documented contract rather than
 * signaling an empty result. Every other error throws, as the sibling
 * endpoints do.
 *
 * @param {string} path           - the export endpoint path
 * @param {string} projectUuid    - the project UUID to export from
 * @param {object} filter         - per-object range map (see getCollectionHexagons)
 * @returns {Promise<string|null>} the storage key, or `null` when nothing passes
 *   the filter
 * @throws {Error} if the response is a 2xx body that isn't the empty-string 204
 *   case and doesn't carry a valid storage key
 */
const postCollectionExport = async (path, projectUuid, filter) => {
	const { data, error } = await apiClientV2.post(path, { project_uuid: projectUuid, filter });
	if (error) throw error;
	if (data === '') return null;
	if (!data || typeof data !== 'object') {
		throw new Error('Invalid collection export response');
	}
	const key = unwrapV2Response(data)?.key;
	if (typeof key !== 'string' || !key) {
		throw new Error('Collection export response is missing a storage key');
	}
	return key;
};

/**
 * Export a collection's detections to one GeoJSON file, for a project.
 * POST /collections/{collectionUuid}/detections/export
 *
 * The server writes the file and answers with its storage key. It does not
 * answer with the file body — the caller signs the key through the file service
 * (`filelink?key=…`) and downloads from the signed link, the same two-step the
 * model path's `getDetections` takes.
 *
 * The body is identical to {@link getCollectionHexagons} and
 * {@link getCollectionDsp} — the three endpoints share one request schema — so
 * build the `filter` with `buildHexagonFilterBody` and the exported file holds
 * exactly the detections the map and the tree show.
 *
 * Every request writes a new file, stamped with its own export instant, so an
 * export never overwrites an earlier one.
 *
 * 204 (a detected area exists, but no detection passes the filter) resolves to
 * `null` rather than throwing: the caller tells the user there is nothing to
 * download. Every other error throws, as the sibling endpoints do.
 *
 * @param {string} projectUuid    - the project UUID to export from
 * @param {string} collectionUuid - the collection UUID
 * @param {object} [filter={}]    - per-object range map (see getCollectionHexagons)
 * @returns {Promise<string|null>} the storage key, or `null` when nothing passes
 *   the filter
 */
export const exportCollectionDetections = (projectUuid, collectionUuid, filter = {}) =>
	postCollectionExport(`/collections/${collectionUuid}/detections/export`, projectUuid, filter);

/**
 * Export a collection's sampling plots to one zip file, for a project.
 * POST /collections/{collectionUuid}/dsp/export
 *
 * The zip holds the plot summary {@link getCollectionDsp} draws, one GeoJSON
 * file per object holding the detections behind those figures, and a manifest.
 * Summary and per-object files count the same detections under the same filter,
 * so the download and the map agree.
 *
 * Key handling, body and 204 match {@link exportCollectionDetections}. Single
 * image only, like the aggregation it exports: an orthophoto project or
 * collection is rejected with 422. Callers mount the control on the DSP layer,
 * which single-image already gates, so that rejection should not be reachable
 * from the UI.
 *
 * @param {string} projectUuid    - the project UUID to export from
 * @param {string} collectionUuid - the collection UUID
 * @param {object} [filter={}]    - per-object range map (see getCollectionHexagons)
 * @returns {Promise<string|null>} the storage key, or `null` when nothing passes
 *   the filter
 */
export const exportCollectionDsp = (projectUuid, collectionUuid, filter = {}) =>
	postCollectionExport(`/collections/${collectionUuid}/dsp/export`, projectUuid, filter);
